
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { COMPANY_INFO, ROUTES, ASSETS, EXTERNAL_LINKS } from '../../constants';
import { Twitter, Send, Instagram, ArrowUp } from 'lucide-react';

export const Footer: React.FC = () => {
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#050508] border-t border-gray-900 pt-16 pb-8 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">

          {/* Column 1: Brand */}
          <div className="col-span-1">
            <h3 className="font-bold text-xl mb-4 flex items-center group cursor-default">
              <img src={ASSETS.LOGO_COMPANY} alt="ctc-logo" className="w-12 h-12 mr-2 object-contain transition-transform duration-300 group-hover:scale-105" />
              <span className="text-white">CTC</span>
              <span className="text-orange-500 ml-1 transition-all duration-300 group-hover:text-orange-400 group-hover:drop-shadow-[0_0_12px_rgba(249,115,22,0.9)]">ChartTower</span>
            </h3>
            <p className="text-gray-400 text-sm mb-6 italic">
              {t('footer.tagline')}
            </p>
            <div className="flex space-x-4">
              <a href={COMPANY_INFO.social.twitter} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-orange-500 hover:text-white transition-all">
                <Twitter size={16} />
              </a>
              <a href={COMPANY_INFO.social.telegram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-blue-500 hover:text-white transition-all">
                <Send size={16} />
              </a>
              <a href={COMPANY_INFO.social.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-pink-600 hover:text-white transition-all">
                <Instagram size={16} />
              </a>
            </div>
          </div>

          {/* Column 2: Divisioni */}
          <div>
            <h4 className="font-bold text-white mb-6 uppercase text-sm tracking-wider">{t('footer.divisions')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to={ROUTES.HOME} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.home')}
                </Link>
              </li>
              <li>
                <Link to={ROUTES.FINANCE} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.finance')}
                </Link>
              </li>
              <li>
                <Link to={ROUTES.REAL_ESTATE} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.realEstate')}
                </Link>
              </li>
              <li>
                <Link to={ROUTES.TOKEN} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.token')}
                </Link>
              </li>
              <li>
                <Link to={ROUTES.AI_AUTOMATION} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('nav.aiAutomation')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Risorse */}
          <div>
            <h4 className="font-bold text-white mb-6 uppercase text-sm tracking-wider">{t('footer.resources')}</h4>
            <ul className="space-y-3">
              <li>
                {/* Link alla Roadmap nella pagina Corporate */}
                <Link to={`${ROUTES.CORPORATE_INFO}#roadmap`} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.roadmap')}
                </Link>
              </li>
              <li>
                {/* Link alla pagina Whitepaper dedicata */}
                <Link to={ROUTES.WHITEPAPER} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.whitepaper')}
                </Link>
              </li>
              <li>
                <a href={EXTERNAL_LINKS.TELEGRAM_ACCESS} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.community')}
                </a>
              </li>
              <li>
                <a href={EXTERNAL_LINKS.RAYDIUM_SWAP} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.liquidity')}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Legal & Contatti */}
          <div>
            <h4 className="font-bold text-white mb-6 uppercase text-sm tracking-wider">{t('footer.legal')}</h4>
            <ul className="space-y-3 mb-6">
              <li>
                <Link to={ROUTES.CORPORATE_INFO} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('nav.infoSocietarie')}
                </Link>
              </li>
              <li>
                {/* New About Link scrolling to anchor */}
                <Link to={`${ROUTES.CORPORATE_INFO}#about`} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.about')}
                </Link>
              </li>
              <li>
                <Link to={ROUTES.PRIVACY} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.privacy')}
                </Link>
              </li>
              <li>
                <Link to={ROUTES.TERMS} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.terms')}
                </Link>
              </li>
              <li>
                <Link to={ROUTES.COOKIE} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {t('footer.items.cookie')}
                </Link>
              </li>
              <li>
                <a href={`mailto:${COMPANY_INFO.email}`} className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  {COMPANY_INFO.email}
                </a>
              </li>
            </ul>
            <p className="text-gray-500 text-xs leading-relaxed">
              {t('footer.address')}
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-900 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-600 text-xs mb-4 md:mb-0">
            {t('footer.copyright')}
          </p>
          <button
            onClick={scrollToTop}
            className="flex items-center text-xs text-gray-500 hover:text-white transition-colors group"
          >
            {t('footer.backToTop')}
            <ArrowUp size={14} className="ml-2 group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </footer>
  );
};
